import {
  Badge,
  Button,
  Checkbox,
  Grid,
  Popover,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFirestore, useFunctions } from 'reactfire';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { groupBy } from 'lodash';
import {
  FilterOption,
  InventoryClass,
  InventoryType,
  NotificationLevel,
  ProductType,
  StandardType,
  UserPermission,
} from '../../enums';
import {
  useAvailableReels,
  useCompany,
  useCompanyCustomParts,
  useCurrentUser,
  useCurrentUserRoles,
  useInventorySearchLogs,
  useNotification,
  usePagination,
  usePermission,
  usePortalSettings,
  useProgress,
  useReservationsAreAllowed,
} from '../../hooks';
import { Reel } from '../../models';
import { DataTable, DataTableColumn } from '../../tables';
import { Filter, InventoryFilters, Response } from '../../types';
import notEmpty from '../../utils/notEmpty';
import { inventoryReportPdf, inventoryReportCsv } from '../../reports';
import {
  ContrastButtonMain,
  ExportMenu,
  MeasurementUnitsToggle,
  ReelQuantity,
  ReelWeight,
  Show,
} from '..';

import ReservationParameters from '../../types/ReservationParameters';
import PartialCut from '../../types/PartialCut';
import { isTodayOrAfter } from '../../utils/dates';
import { mapGroupBy } from '../../utils/array';
import { InventoryTableProps } from './InventoryTable.props';
import { RowCollapse } from './components/RowCollapse/RowCollapse.component';
import * as TableSx from './InventoryTable.styles';
import { isDisabledFunction, isSelectedFunction } from './utils';
import { InventoryTableGroupHeader } from './components/InventoryTableGroupHeader/InventoryTableGroupHeader.component';

export const InventoryTable = memo(
  ({
    type,
    filters,
    setFilters,
    user,
    impersonatedCustomer,
    impersonatedUser,
    openedCollapse,
    setOpenedCollapse,
    sorting,
    measureUnits,
    setMeasureUnits,
    multipleReservations,
    selectedReels,
    setMultipleReservations,
    resetMultipleReservations,
    toggleItemSelection,
    isDesktop = false,
    inventorySearchLogId,
    onLoadProducts,
  }: InventoryTableProps) => {
    const [reserving, setReserving] = useState(false);
    const [partialCutError, setPartialCutError] = useState(false);
    const [openedGroup, setOpenedGroup] = useState<string[]>([]);
    const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
    const impersonating = useRef<boolean>(
      !!impersonatedUser || !!impersonatedCustomer
    );
    const { isAdmin, isAgent, isCorporateUser } = useCurrentUserRoles();
    const { canSeeCSA, canSeeUL } = useCurrentUser();
    const { updateLog } = useInventorySearchLogs();

    const isMobile = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down('sm')
    );

    const navigate = useNavigate();
    const functions = useFunctions();
    const reserveFunction = httpsCallable<ReservationParameters>(
      functions,
      'reserveReel'
    );
    const { hasOneOfPermissions, hasPermission } = usePermission();
    const reservationsAreAllowed = useReservationsAreAllowed();
    const { addNotification } = useNotification();
    const { showProgress } = useProgress();
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingCsv, setExportingCsv] = useState(false);
    const [companyId, setCompanyId] = useState<string>(user.companyRef.id);
    const firestore = useFirestore();
    const { t, i18n } = useTranslation();
    const { item: portalSettingsData } = usePortalSettings();
    const [customPartsFilters, setCustomPartsFilters] = useState<Filter[]>([]);

    const pagination = usePagination(100);

    useEffect(() => {
      if (impersonatedCustomer?.company.companyId) {
        setCompanyId(impersonatedCustomer.company.companyId);
      } else {
        setCompanyId(user.companyRef.id);
      }
    }, [user.companyRef.id, impersonatedCustomer?.company.companyId]);

    const { item: company } = useCompany(companyId);

    useEffect(() => {
      showProgress(reserving);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reserving]);

    useEffect(() => {
      impersonating.current = !!impersonatedUser || !!impersonatedCustomer;
    }, [impersonatedCustomer, impersonatedUser]);

    useEffect(() => {
      if (!filters || !filters.customPartNumber) {
        setCustomPartsFilters([]);
      } else {
        setCustomPartsFilters([['partNumber', '==', filters.customPartNumber]]);
      }
    }, [filters]);

    const { list: parts } = useCompanyCustomParts(companyId, {
      filters: customPartsFilters,
    });

    const canOnlySeeCSA = canSeeCSA && !canSeeUL;
    const canOnlySeeUL = !canSeeCSA && canSeeUL;
    const companyClass = company?.class ?? InventoryClass.ClassC;

    const buildStandardTypeFilter = () => {
      if (canOnlySeeCSA)
        return `${FilterOption.StandardType}=${InventoryType.CSA}`;
      if (canOnlySeeUL)
        return `${FilterOption.StandardType}=${InventoryType.UL}`;
      return null;
    };

    const buildInventoryClassFilter = () =>
      `${FilterOption.InventoryClass}=${companyClass}`;

    const buildCableIdFilter = (id: string) => `${FilterOption.CableId}=${id}`;

    const buildFilterList = (filters: InventoryFilters) => {
      const list = [
        `${FilterOption.StandardType}=${filters.standardType}`,
        buildInventoryClassFilter(),
        filters.productType &&
          `${FilterOption.ProductType}=${filters.productType}`,
        filters.productSubType &&
          filters.productSubType !== 'ALL' &&
          `${FilterOption.ProductSubType}=${filters.productSubType}`,
        filters.material && `${FilterOption.MaterialType}=${filters.material}`,
        filters.voltage &&
          `${FilterOption.Voltage}=${
            filters.voltage === 'N/A' ? -1 : filters.voltage
          }`,
        filters.productType !== ProductType.BareGround &&
          filters.numberOfConductors &&
          `${FilterOption.NumberOfConductors}=${filters.numberOfConductors}`,
        filters.gauge && `${FilterOption.Gauge}=${filters.gauge}`,
      ];
      return list.filter(notEmpty).join(';');
    };

    const queryFilters = useMemo(() => {
      if (!filters) return undefined;

      // Search by part number or custom part number (no parts found)
      if (
        !filters.searchByCable &&
        (filters.partNumber || (filters.customPartNumber && parts.length === 0))
      ) {
        const filterList = [
          buildStandardTypeFilter(),
          buildInventoryClassFilter(),
          filters.customPartNumber
            ? buildCableIdFilter(filters.customPartNumber)
            : filters.partNumber
            ? buildCableIdFilter(filters.partNumber)
            : null,
        ].filter(notEmpty);

        return [
          ['isVisible', '!=', false],
          ['filters', 'array-contains', filterList.join(';')],
        ] as Filter[];
      }

      // Custom part number with available parts
      if (!filters.searchByCable && filters.customPartNumber) {
        const filterList = parts.map((part) =>
          [
            buildStandardTypeFilter(),
            buildInventoryClassFilter(),
            buildCableIdFilter(part.normalizedId),
          ]
            .filter(notEmpty)
            .join(';')
        );

        return [
          ['isVisible', '!=', false],
          ['filters', 'array-contains-any', filterList],
        ] as Filter[];
      }

      // General case
      const filterList = buildFilterList(filters);

      return [
        ['isVisible', '!=', false],
        ['filters', 'array-contains', filterList],
      ] as Filter[];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, parts]);

    const { list: cables, loading } = useAvailableReels({
      filters: queryFilters,
      pagination,
      sorting,
    });

    useEffect(() => {
      if (filters && !loading && inventorySearchLogId) {
        if (cables.length === 0) {
          updateLog(inventorySearchLogId, {
            yieldedResults: false,
            lowInventory: false,
          });
        } else if (portalSettingsData?.lowInventoryThreshold) {
          let count = 0;
          let lowInventory = true;
          for (let i = 0; i < cables.length; i++) {
            count += cables[i].quantity;
            if (count >= portalSettingsData.lowInventoryThreshold) {
              lowInventory = false;
              break;
            }
          }

          updateLog(inventorySearchLogId, {
            yieldedResults: true,
            lowInventory,
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cables, loading, filters, inventorySearchLogId]);

    const reserveReel = (
      reservedReel: Reel,
      note: string,
      packagesAmount: number,
      partialCut: PartialCut | undefined,
      packageIncrement: number | undefined
    ) => {
      setReserving(true);
      addNotification(
        t('inventory.reserveInfo'),
        NotificationLevel.Information,
        impersonating.current
      );

      reserveFunction({
        reelId: reservedReel.id,
        impersonatedUserId: impersonatedUser?.id,
        inventoryType: type,
        madeForCompanyId: impersonatedCustomer?.company.companyId,
        madeForCustomerName: impersonatedCustomer?.personName,
        note,
        packagesAmount,
        partialCut,
      })
        .then((res) => {
          if (!(res.data as Response).succeeded) {
            addNotification(
              t('inventory.reserveNotAvailable'),
              NotificationLevel.Error,
              impersonating.current
            );
          } else {
            if (inventorySearchLogId) {
              updateLog(inventorySearchLogId, {
                reservationWasMade: true,
              });
            }
            addNotification(
              t('inventory.reserveSuccess'),
              NotificationLevel.Success,
              impersonating.current
            );
          }
        })
        .catch((error) => {
          let message = '';
          if (
            error.code === 'functions/invalid-argument' &&
            error.message.startsWith('Not enough cable left')
          ) {
            message = t('inventory.reserveTooBigPartialCutWarning');
            setPartialCutError(true);
          } else if (
            error.code === 'functions/invalid-argument' &&
            error.message.startsWith('Package increment error')
          ) {
            message = t('inventory.packageIncrementError', {
              number: packageIncrement,
            });
            setPartialCutError(true);
          } else {
            message = t('inventory.reserveError');
          }
          addNotification(
            message,
            NotificationLevel.Error,
            impersonating.current
          );
        })
        .finally(() => {
          setReserving(false);
        });
    };

    const getUniqueKeyFromReel = useCallback((c: Reel) => c.cableRef.id, []);

    const reelDescription = (description: string, item: Reel) => {
      if (item.colors) {
        return (
          <>
            <div>
              {description} - {item.cableRef.id}
            </div>
            <div>{item.colors}</div>
          </>
        );
      }

      return (
        <>
          {description} - {item.cableRef.id}
        </>
      );
    };

    useEffect(() => {
      if (!filters || filters.searchByCable || loading || !cables) return;

      setFilters({
        ...filters,
        productType: cables.length > 0 ? cables[0].productType : null,
      });
      if (cables.length > 0) {
        // After applying filters, change the tab to CSA or UL to match products found.
        const typeFound: number = cables[0].standardType ?? StandardType.Both;
        onLoadProducts(typeFound);
      }
      // This component is only rendered if filters is not null, therefore:
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters?.productType, cables, loading]);

    useEffect(() => {
      if (!(exportingPdf || exportingCsv) || !queryFilters || !filters) return;

      const q = query(
        collection(firestore, 'AvailableReels'),
        ...queryFilters.map((queryFilter) =>
          where(queryFilter[0], queryFilter[1], queryFilter[2])
        ),
        ...[
          orderBy('isVisible', 'asc'),
          ...sorting.asQueryOrderByConstraints(),
          ...(sorting.sortField !== 'quantity'
            ? [orderBy('quantity', 'desc')]
            : []),
        ]
      );

      getDocs(q)
        .then((res) => {
          const results = res.docs.map((doc) => doc.data() as Reel);
          const groupedResults = mapGroupBy(results, getUniqueKeyFromReel);
          const flatGroupedResults = Array.from(groupedResults.values()).flat();

          if (exportingPdf)
            inventoryReportPdf.generate(
              flatGroupedResults,
              filters,
              measureUnits,
              type,
              t,
              i18n
            );

          if (exportingCsv)
            inventoryReportCsv.generate(
              flatGroupedResults,
              filters,
              measureUnits,
              t
            );
        })
        .finally(() => {
          setExportingPdf(false);
          setExportingCsv(false);
        });
    }, [
      exportingCsv,
      exportingPdf,
      filters,
      firestore,
      getUniqueKeyFromReel,
      i18n,
      measureUnits,
      queryFilters,
      sorting,
      t,
      type,
    ]);

    const handleCloseMultipleReservations = () => {
      setAnchorEl(null);
      resetMultipleReservations();
    };

    const shouldNavigateToReserve = useMemo(() => {
      if (
        isAdmin &&
        hasPermission(UserPermission.ImpersonateCustomer) &&
        !impersonatedUser &&
        !impersonatedCustomer
      ) {
        return true;
      }

      if (
        isAgent &&
        hasPermission(UserPermission.ManageOwnReservations) &&
        !impersonatedCustomer
      ) {
        return true;
      }

      if (
        isCorporateUser &&
        hasPermission(UserPermission.ManageOwnReservations) &&
        !impersonatedCustomer
      ) {
        return true;
      }

      return false;
    }, [
      hasPermission,
      impersonatedCustomer,
      impersonatedUser,
      isAdmin,
      isAgent,
      isCorporateUser,
    ]);

    useEffect(() => {
      if (multipleReservations && shouldNavigateToReserve) {
        navigate('reserve');
      }
    }, [multipleReservations, navigate, shouldNavigateToReserve]);

    const columns: { [key: string]: JSX.Element | undefined } = {
      selected: (
        <DataTableColumn
          key="selected-column"
          property="multipleSelection"
          disableSort
          textAlign="center"
          output={(_, item: Reel) => {
            if (isDisabledFunction(item)) {
              return (
                <Tooltip
                  title={
                    <Typography>
                      {t('inventory.multipleReservations.disabled')}
                    </Typography>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'black',
                        textAlign: 'center',
                      },
                    },
                    arrow: {
                      sx: {
                        color: 'black',
                      },
                    },
                  }}
                >
                  <InfoOutlinedIcon />
                </Tooltip>
              );
            } else {
              return (
                <Checkbox
                  id={item.id}
                  color="secondary"
                  checked={isSelectedFunction(selectedReels, item)}
                />
              );
            }
          }}
        />
      ),
      partsNumber: (
        <DataTableColumn
          key="parts-number-column"
          property="description"
          title={t('inventory.fields.partNumber')}
          mobileWidth="calc(100% - 9rem)"
          mobileOrder={0}
          output={(description, item: Reel) =>
            reelDescription(description, item)
          }
        />
      ),
      customParts: (
        <DataTableColumn
          key="custom-parts-number-column"
          property="partNumber"
          title={t('inventory.fields.customPartNumber')}
          hidden={
            parts.length === 0 || (!isAdmin && !company?.hasCustomPartNumbers)
          }
          output={(description, item: Reel) => {
            const customPart = parts.find(
              (part) => part.id === item.cableRef.id
            );
            return <>{customPart?.partNumber ?? ''}</>;
          }}
        />
      ),
      quantity: (
        <DataTableColumn
          key="quantity-column"
          property="quantity"
          title={t('inventory.fields.quantity')}
          hasMobileLabel={false}
          mobileOrder={1}
          output={(_, item: Reel) => (
            <ReelQuantity item={item} unitsType={measureUnits} />
          )}
        />
      ),
      reel: (
        <DataTableColumn
          key="reel-column"
          property="reel"
          title={t('inventory.fields.reel')}
          mobileHidden
          mobileVisibleWhenActive
          mobileWidth="50%"
        />
      ),
      weight: (
        <DataTableColumn
          key="weight-column"
          property="weight"
          title={t('inventory.fields.weight')}
          mobileHidden
          mobileVisibleWhenActive
          mobileWidth="50%"
          output={(_, item: Reel) => (
            <ReelWeight item={item} unitsType={measureUnits} />
          )}
        />
      ),
      reelId: (
        <DataTableColumn
          key="reel-id-column"
          property="id"
          title={t('inventory.fields.reelId')}
          mobileHidden
        />
      ),
      reserveButton:
        (hasPermission(UserPermission.ManageOwnReservations) ||
          hasPermission(UserPermission.ImpersonateCustomer)) &&
        reservationsAreAllowed ? (
          <DataTableColumn
            key="reserve-column"
            property="action"
            title=""
            disableSort
            mobileHeight={0}
            mobileWidth="12rem"
            mobileOrder={0}
            textAlign="left"
            mobileTextAlign="right"
            output={(_, item) => (
              <Button
                sx={{ display: 'inline-block' }}
                variant="contained"
                onClick={(e) => {
                  if (shouldNavigateToReserve) {
                    navigate('reserve');
                  }

                  setOpenedCollapse(item.id);
                  e.stopPropagation();
                }}
                disabled={
                  (openedCollapse === item.id &&
                    ((!isAgent &&
                      !isCorporateUser &&
                      hasPermission(UserPermission.ManageOwnReservations)) ||
                      (hasPermission(UserPermission.ImpersonateCustomer) &&
                        (!!impersonatedUser || !!impersonatedCustomer)) ||
                      (hasPermission(UserPermission.ManageOwnReservations) &&
                        !!impersonatedCustomer))) ||
                  reserving ||
                  (user && !impersonatedUser
                    ? user.reservationCount >= user.maxReservationsPerPeriod
                    : false) ||
                  (impersonatedUser?.reservationCount || 0) +
                    (impersonatedUser?.reservationByNCCount || 0) >=
                    99
                }
              >
                {t('inventory.actions.reserve')}
              </Button>
            )}
          />
        ) : undefined,
      added: (
        <DataTableColumn
          key="added-column"
          property="added"
          disableSort
          mobileHidden
          width="8%"
          output={(_, item: Reel) => (
            <Typography fontWeight={'bold'}>
              {isSelectedFunction(selectedReels, item)
                ? t('inventory.fields.added')
                : ''}
            </Typography>
          )}
        />
      ),
    };

    const showMultipleReservationsSwitch = useMemo(
      () =>
        isDesktop &&
        !!reservationsAreAllowed &&
        !!hasOneOfPermissions([
          UserPermission.ManageOwnReservations,
          UserPermission.ImpersonateCustomer,
        ]),
      [hasOneOfPermissions, isDesktop, reservationsAreAllowed]
    );

    const hideMultipleReservationsPillExpiry = useMemo(() => {
      if (portalSettingsData?.multipleReservationsPillExpiry) {
        return !isTodayOrAfter(
          portalSettingsData?.multipleReservationsPillExpiry
        );
      }

      return false;
    }, [portalSettingsData?.multipleReservationsPillExpiry]);

    useEffect(() => {
      const shouldExpandAll = filters?.searchByCable
        ? filters.gauge &&
          filters.material &&
          filters.voltage &&
          filters.numberOfConductors &&
          filters.productType &&
          filters.standardType
        : filters?.partNumber || filters?.customPartNumber;

      if (!shouldExpandAll) {
        setOpenedGroup([]);
      } else {
        const groups = groupBy(cables, getUniqueKeyFromReel);
        setOpenedGroup(Object.keys(groups));
      }
    }, [cables, filters, getUniqueKeyFromReel]);

    return (
      <>
        <Show if={cables.length > 0}>
          <Grid container mt={1} justifyContent={'space-between'}>
            <Show if={showMultipleReservationsSwitch}>
              <Grid
                container
                item
                justifyContent="flex-start"
                alignItems="center"
                md={4}
                xs={12}
              >
                <TableSx.StyledSwitch
                  id="multiple-reservations-switch"
                  checked={multipleReservations}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      setMultipleReservations(true);
                    } else if (selectedReels.length) {
                      setAnchorEl(e.currentTarget);
                    } else {
                      handleCloseMultipleReservations();
                    }
                  }}
                />
                <Badge
                  invisible={hideMultipleReservationsPillExpiry}
                  sx={TableSx.BadgeStyles(i18n)}
                  badgeContent={
                    <TableSx.StyledChip
                      label={t('common.new')}
                      variant="outlined"
                      size="small"
                      color="secondary"
                    />
                  }
                >
                  <Typography
                    variant="body2"
                    sx={TableSx.multipleReservationsToggleStyles(
                      i18n,
                      multipleReservations
                    )}
                  >
                    {t('inventory.filters.multiProductReservation')}
                  </Typography>
                </Badge>
              </Grid>
              <Popover
                open={anchorEl !== null}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <Stack spacing={2} p={2} maxWidth={400}>
                  <Typography fontWeight="bold">
                    {t('inventory.multipleReservations.close.title')}
                  </Typography>
                  <Typography>
                    {t('inventory.multipleReservations.close.body')}
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="space-around"
                    spacing={1}
                  >
                    <Button
                      variant="contained"
                      sx={{ paddingX: 5 }}
                      onClick={() => setAnchorEl(null)}
                    >
                      {t('inventory.actions.cancel')}
                    </Button>
                    <ContrastButtonMain
                      onClick={handleCloseMultipleReservations}
                    >
                      {t('inventory.multipleReservations.close.turnOff')}
                    </ContrastButtonMain>
                  </Stack>
                </Stack>
              </Popover>
            </Show>

            <Grid
              container
              item
              md={showMultipleReservationsSwitch ? 8 : 12}
              justifyContent={isMobile ? 'flex-start' : 'flex-end'}
            >
              <Grid item justifyContent="center" display="flex" pr={1}>
                <MeasurementUnitsToggle
                  units={measureUnits}
                  setUnits={(value) =>
                    setMeasureUnits((prev) => (value ? (prev = value) : prev))
                  }
                />
              </Grid>
              <Grid display="flex" justifyContent="flex-end">
                <ExportMenu
                  onPDFClicked={() => setExportingPdf(true)}
                  onCSVClicked={() => setExportingCsv(true)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Show>

        <DataTable
          openedGroup={openedGroup}
          setOpenedGroup={setOpenedGroup}
          groupKey={getUniqueKeyFromReel}
          renderGroupHeader={(items, isOpen) => (
            <InventoryTableGroupHeader
              isMultipleReservations={multipleReservations}
              groupedData={items}
              isOpen={isOpen}
              measureUnits={measureUnits}
            />
          )}
          data={queryFilters && !loading ? cables : []}
          isDisabledFunction={isDisabledFunction}
          isSelectedFunction={isSelectedFunction}
          selected={selectedReels}
          loading={loading}
          pagination={pagination}
          sorting={sorting}
          tableStyles={multipleReservations ? TableSx.tableStyles : undefined}
          tableBodyStyles={
            multipleReservations ? TableSx.tableBodyStyles : undefined
          }
          rowOnClick={
            multipleReservations
              ? (item) => toggleItemSelection(item)
              : undefined
          }
          rowCollapseContent={
            multipleReservations
              ? undefined
              : (item) => (
                  <RowCollapse
                    item={item}
                    openedCollapse={openedCollapse}
                    partialCutError={partialCutError}
                    reserve={reserveReel}
                    reserving={reserving}
                    reservingCustomer={impersonatedCustomer}
                    reservingUser={impersonatedUser ?? user}
                    setOpenedCollapse={setOpenedCollapse}
                    setPartialCutError={setPartialCutError}
                    units={measureUnits}
                  />
                )
          }
          rowCollapseOpened={
            multipleReservations
              ? undefined
              : (item: Reel) =>
                  item.id === openedCollapse &&
                  openedGroup.includes(getUniqueKeyFromReel(item)) &&
                  ((hasPermission(UserPermission.ImpersonateCustomer) &&
                    (!!impersonatedUser || !!impersonatedCustomer)) ||
                    ((isAgent || isCorporateUser) &&
                      hasPermission(UserPermission.ManageOwnReservations) &&
                      !!impersonatedCustomer) ||
                    (!!hasPermission(UserPermission.ManageOwnReservations) &&
                      !(isAdmin || isAgent || isCorporateUser)))
          }
        >
          {multipleReservations
            ? [
                columns.selected,
                columns.partsNumber,
                columns.customParts,
                columns.quantity,
                columns.reel,
                columns.weight,
                columns.reelId,
                columns.added,
              ]
            : [
                columns.partsNumber,
                columns.customParts,
                columns.quantity,
                columns.reel,
                columns.weight,
                columns.reelId,
                columns.reserveButton,
              ]}
        </DataTable>
      </>
    );
  }
);
